import React, { useState } from 'react';
import axios from 'axios';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import Button from './ui/Button';
import { Input } from './pilot-ui/input';
import { Label } from './pilot-ui/label';

const CLAIM_CODE = '1c64a730-e1fc-499f-904b-41d24935a955';
const USER_UUID = '550e8400-e29b-41d4-a716-446655440000';
const THING_NAME_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

const Enroll: React.FC = () => {
  const [thingName, setThingName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [rawResponse, setRawResponse] = useState<Record<string, unknown> | null>(null);

  const isValid = THING_NAME_PATTERN.test(thingName);

  const executeAction = async (
    action: () => Promise<{ data: Record<string, unknown> }>,
    successMsg: string,
    errorPrefix: string
  ) => {
    setLoading(true);
    setMessage(null);
    setRawResponse(null);

    try {
      const response = await action();
      setRawResponse(response.data);
      setMessage({ type: 'success', text: successMsg });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setRawResponse(err.response?.data || null);
        const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Unknown error';
        setMessage({ type: 'error', text: `${errorPrefix}: ${errorMsg}` });
      } else {
        setMessage({ type: 'error', text: `${errorPrefix}: Unknown error` });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    await executeAction(
      () => axiosInstance.post(config.claimThingApiUrl!, {
        thing_name: thingName,
        claim_code: CLAIM_CODE,
        user_uuid: USER_UUID,
      }),
      `Successfully claimed "${thingName}".`,
      `Failed to claim "${thingName}"`
    );
  };

  const handleCheck = async () => {
    if (!isValid) return;

    setLoading(true);
    setMessage(null);
    setRawResponse(null);

    try {
      const response = await axiosInstance.get<{ enrolled: boolean }>(`${config.checkEnrollmentApiUrl!}/${thingName}`);
      setRawResponse(response.data as unknown as Record<string, unknown>);
      const enrolled = response.data?.enrolled;
      setMessage({
        type: enrolled ? 'success' : 'error',
        text: enrolled
          ? `"${thingName}" is enrolled and ready to use.`
          : `"${thingName}" is NOT fully enrolled.`,
      });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setRawResponse(err.response?.data || null);
        const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Unknown error';
        setMessage({ type: 'error', text: `Failed to check enrollment for "${thingName}": ${errorMsg}` });
      } else {
        setMessage({ type: 'error', text: `Failed to check enrollment for "${thingName}": Unknown error` });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnenroll = async () => {
    if (!isValid) return;
    await executeAction(
      () => axiosInstance.post(config.resetThingApiUrl!, { thing_name: thingName }),
      `Successfully unenrolled "${thingName}".`,
      `Failed to unenroll "${thingName}"`
    );
  };

  return (
    <div className="p-6 max-w-lg">
      <h1 className="text-2xl font-bold mb-4">Enroll Device</h1>
      <p className="text-gray-600 mb-6">
        <strong>Claim</strong> registers a device ID with the U-BLOX staging broker so it will connect to our OEM endpoint.
        The actual IoT Core enrollment happens automatically via JITP when the device first connects.
        <strong>Check</strong> verifies whether the device has completed enrollment.
        <strong>Unenroll</strong> tears down the IoT Core thing, certificates, and policy attachments.
      </p>

      <form onSubmit={handleEnroll} className="space-y-4">
        <div>
          <Label htmlFor="thingName" className="mb-1">
            Thing Name
          </Label>
          <p id="thing-name-help" className="text-xs text-muted-foreground mb-1">Enter the device ID printed on the unit. Use letters, numbers, hyphens or underscores.</p>
          <Input
            id="thingName"
            type="text"
            value={thingName}
            onChange={(e) => setThingName(e.target.value)}
            aria-describedby={thingName && !isValid ? 'thing-name-help thing-name-error' : 'thing-name-help'}
            aria-invalid={thingName && !isValid ? true : undefined}
            placeholder="e.g. DL-0019"
            className="block w-full"
            disabled={loading}
          />
          {thingName && !isValid && (
            <p id="thing-name-error" className="mt-1 text-sm text-red-700">
              Must be 1-128 characters: letters, numbers, hyphens, or underscores.
            </p>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            variant="primary"
            type="submit"
            disabled={!isValid || loading}
            title="Register this device ID with the U-BLOX staging broker so the device connects to our endpoint; enrollment then happens on its first connection"
          >
            {loading ? 'Processing...' : 'Claim'}
          </Button>
          <Button
            variant="secondary"
            type="button"
            onClick={handleCheck}
            disabled={!isValid || loading}
            title="Ask AWS IoT Core whether this device has finished enrolling; changes nothing"
          >
            {loading ? 'Processing...' : 'Check'}
          </Button>
          <Button variant="danger" type="button" onClick={handleUnenroll}
                  disabled={!isValid || loading}
                  title="Remove this device's IoT Core thing, certificates, and policy attachments.">
            {loading ? 'Processing...' : 'Unenroll'}
          </Button>
        </div>
      </form>

      {message && (
        <div role={message.type === 'error' ? 'alert' : 'status'} className={`mt-4 border p-3 rounded-md ${message.type === 'success' ? 'border-status-success-border bg-status-success text-status-success-foreground' : 'border-status-error-border bg-status-error text-status-error-foreground'}`}>
          {message.text}
        </div>
      )}

      {rawResponse && (
        <div className="mt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-1">Response</h3>
          <pre className="p-3 bg-gray-900 text-green-400 rounded-md text-xs overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(rawResponse, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default Enroll;
